import React, { Component, createRef } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { PaymentService } from "@services";
import utils from "@utils";
import Modal from "../Modal/Modal";
import Loader from "../Loader/Loader";

import "./ApplyPaymentsModal.scss";

class ApplyPaymentsModal extends Component {
	constructor(props) {
		super(props);

		this.initialState = {
			invoiceId: null,
			loading: false,
			transactionType: "apply",
			isOpen: false,
			comments: "",
			isCommentsInvalid: false,
			amount: "",
			isAmountInvalid: false,
			paymentHistory: undefined,
			isFromPaymentPortal: false,
		};

		this.state = { ...this.initialState };
		this.amountInput = createRef();
	}

	/**
	 * Opens the modal and retrieves a list of all payments made against this manual invoice
	 * @param {Number} invoiceId The ID number for the manual invoice
	 */
	openModal = invoiceId => {
		this.setState({ isOpen: true, invoiceId }, () => {
			this.getPayments(invoiceId);
		});
	};

	/**
	 * Retrieves a list of the payment history of a given invoice
	 * @param {Number} invoiceId he ID number for the manual invoice
	 */
	getPayments = invoiceId => {
		const { lang } = global;
		this.setState({ loading: true });
		const { refundsInTransit } = this.props;

		PaymentService.getNonRKPayInvoicePayments(invoiceId)
			.then(({ data }) => {
				let paymentHistory = data.map(transaction => ({ ...transaction, _checked: false }));
				paymentHistory = [...paymentHistory, ...refundsInTransit];
				const isFromPaymentPortal = data.some(item => item.PaymentMethodType !== null);
				this.setState({ paymentHistory, loading: false, isFromPaymentPortal });
			})
			.catch(() => {
				this.setState({ loading: false });
				utils.notify({
					type: "error",
					message: lang.t("Error getting payment history"),
					title: "Error",
				});
			});
	};

	closeModal = () => this.setState(this.initialState);

	/** Applies a new payment along with provided comments to a manual invoice */
	addInvoicePayment = () => {
		const { lang } = global;
		const { comments, amount, invoiceId } = this.state;
		PaymentService.createNonRKPayInvoicePayment(invoiceId, { amount, comments })
			.then(() => {
				utils.notify({
					type: "success",
					message: lang.t("Payment applied successfully"),
				});
				this.updateTotalAmount("apply", amount);
				this.getPayments(invoiceId);
				this.setState({
					comments: "",
					amount: "",
					isCommentsInvalid: false,
					isAmountInvalid: false,
				});
				if (this.amountInput.current) this.amountInput.current.focus();
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error applying payment"),
					title: "Error",
				});
			});
	};

	/** Removes payment(s) from a manual invoice */
	removeInvoicePayments = () => {
		const { lang } = global;
		const { paymentHistory, invoiceId } = this.state;
		const paymentsToUnapply = [];
		let unappliedAmount = 0;

		paymentHistory.forEach(payment => {
			if (payment._checked) {
				paymentsToUnapply.push(payment.Id);
				unappliedAmount += payment.Amount;
			}
		});
		PaymentService.deleteNonRKPayInvoicePayments(invoiceId, paymentsToUnapply)
			.then(() => {
				utils.notify({
					type: "success",
					message: lang.t("Payment(s) unapplied successfully"),
				});
				this.updateTotalAmount("unapply", unappliedAmount);
				this.getPayments(invoiceId);
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error unapplying payment(s)"),
					title: "Error",
				});
			});
	};

	/**
	 * Creates an object with the ID of the manual invoice along with the new applied payment total and passes it to the prop `callback`
	 * @param {'apply'|'unapply'} type The type of action that was taken prior to this method being called
	 * @param {String|Number} amount The amount that was applied or unapplied from the manual invoice
	 */
	updateTotalAmount = (type, amount) => {
		const { callback } = this.props;
		const { invoiceId, paymentHistory } = this.state;
		const currentApplied = paymentHistory.reduce((a, b) => a + (b.Amount || 0), 0);

		if (callback) {
			const totalApplied =
				type === "apply"
					? currentApplied + parseFloat(amount)
					: currentApplied - parseFloat(amount);
			callback({ id: invoiceId, totalApplied });
		}
	};

	/**
	 * Handles changes to all radio buttons and input fields in the modal
	 * @param {Event} event The HTML event handled by this method
	 */
	updateInput = ({ target: { name, value: rawValue } }) => {
		let value = rawValue;
		if (name === "amount") value = rawValue.replace(/[^-0-9.]/gi, "");
		this.setState({ [name]: value }, () => {
			if (name === "transactionType" && value === "apply" && this.amountInput.current) {
				this.amountInput.current.focus();
			}
		});
	};

	toggleCheck = ({ target: { name: index } }) => {
		const { paymentHistory } = this.state;
		const newPaymentHistory = [...paymentHistory];

		newPaymentHistory[index] = {
			...paymentHistory[index],
			_checked: !newPaymentHistory[index]._checked,
		};

		this.setState({ paymentHistory: newPaymentHistory });
	};

	onKeyPress = ({ key }) => {
		if (key === "Enter") {
			this.validateInputs();
		}
	};

	/** Validates that all necessary inputs are appropriately filled out based on the selected transaction type */
	validateInputs = () => {
		const { lang } = global;
		const { transactionType, amount, comments, paymentHistory } = this.state;

		if (transactionType === "apply") {
			const isAmountInvalid = Number.isNaN(Number(amount)) || amount === "";
			const isCommentsInvalid = comments === "";

			if (isAmountInvalid || isCommentsInvalid) {
				this.setState({ isAmountInvalid, isCommentsInvalid });
			} else {
				this.addInvoicePayment();
			}
		}

		if (transactionType === "unapply") {
			if (paymentHistory.some(payment => payment._checked === true)) {
				this.removeInvoicePayments();
			} else {
				utils.notify({
					type: "error",
					message: lang.t("Select at least one payment to be unapplied"),
				});
			}
		}
	};

	renderStatusLabel = status => {
		let label;

		/*
		 *** Payment Statuses ***
		 * Created = 1
		 * Pending = 2
		 * Failed = 3
		 * Succeeded = 4
		 * Refunded = 5
		 * PendingRefund = 6
		 */

		switch (status) {
			case 1:
			case 4:
			case 5:
				label = "label-success";
				break;
			case 3:
				label = "label-danger";
				break;
			default:
				label = "label-warning";
				break;
		}

		return label;
	};

	render() {
		const { lang } = global;
		const { user, invoiceVersion, disabledManualPayments } = this.props;
		const {
			transactionType,
			paymentHistory,
			isFromPaymentPortal,
			isOpen,
			amount,
			comments,
			loading,
			isAmountInvalid,
			isCommentsInvalid,
		} = this.state;

		return (
			<Modal
				id="non-rkpay-payments-modal"
				show={isOpen}
				onHide={this.closeModal}
				onEntered={() => {
					if (this.amountInput.current) this.amountInput.current.focus();
				}}
			>
				<Modal.Header>
					<Modal.Title>{lang.t("Payment History")}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{/* As part of invoicing V3 (see PBI-962) we will only be display the payment history for those users */}
					<If condition={invoiceVersion !== 3 && !disabledManualPayments}>
						<div className="row text-center">
							<If condition={!isFromPaymentPortal}>
								<div className="col-xs-6 radio">
									<label htmlFor="applyPayment">
										<input
											id="applyPayment"
											type="radio"
											name="transactionType"
											value="apply"
											checked={transactionType === "apply"}
											onChange={this.updateInput}
										/>
										{lang.t("Apply Payment")}
									</label>
								</div>
								<div className="col-xs-6 radio">
									<label htmlFor="unapplyPayment">
										<input
											id="unapplyPayment"
											type="radio"
											name="transactionType"
											value="unapply"
											checked={transactionType === "unapply"}
											onChange={this.updateInput}
										/>
										{lang.t("Unapply Payment")}
									</label>
								</div>
							</If>
						</div>
						<If
							condition={
								transactionType === "apply" &&
								user?.Organization?.NonRKPayPaymentsEnabled &&
								paymentHistory?.length === 0
							}
						>
							<div className="alert bg-warning text-center">
								<i className="glyphicon glyphicon-info-sign" />
								&nbsp;
								{lang.t(
									"Clients will no longer be able to pay online if you apply a manual payment to this invoice_",
								)}
							</div>
						</If>
						<If condition={transactionType === "apply" && !isFromPaymentPortal}>
							<div className="row v-align add-payment">
								<div
									className={classNames("col-xs-3", "form-group", {
										"has-error": isAmountInvalid,
									})}
								>
									<label htmlFor="amount">{lang.t("Amount")}</label>
									<input
										id="amount"
										name="amount"
										className="form-control"
										type="tel"
										value={amount}
										onChange={this.updateInput}
										onKeyPress={this.onKeyPress}
										ref={this.amountInput}
									/>
								</div>
								<div
									className={classNames("col-xs-6 col-sm-7", "form-group", {
										"has-error": isCommentsInvalid,
									})}
								>
									<label htmlFor="comments">{lang.t("Comments")}</label>
									<input
										id="comments"
										name="comments"
										className="form-control"
										type="text"
										value={comments}
										onChange={this.updateInput}
										onKeyPress={this.onKeyPress}
									/>
								</div>
								<div className="col-xs-3 col-sm-2 text-right">
									<button
										type="button"
										className="btn btn-primary"
										onClick={this.validateInputs}
										disabled={loading}
									>
										{lang.t("Apply")}
									</button>
								</div>
							</div>
						</If>
					</If>
					<Choose>
						<When condition={paymentHistory?.length > 0}>
							<div
								id="table-header"
								className="row hidden-xs"
							>
								<div
									className={classNames("col-xs-3 col-sm-2", {
										"col-xs-offset-1": transactionType === "unapply",
									})}
								>
									{lang.t("Date")}
								</div>
								<div className="col-xs-3 col-sm-2">{lang.t("Amount")}</div>
								<div className="hidden-xs col-sm-3">{lang.t("Created By")}</div>
								<div
									className={
										transactionType === "unapply"
											? "col-xs-4"
											: "col-xs-6 col-sm-5"
									}
								>
									{lang.t("Comments")}
								</div>
							</div>
							<div className="payment-history-table">
								<For
									each="payment"
									of={paymentHistory}
									index="index"
								>
									<div
										key={payment.Id}
										className="row payment-record"
									>
										<div
											className={`col-xs-2 col-sm-1 text-center ${transactionType === "unapply" ? "" : "hidden"}`}
										>
											<If
												condition={
													transactionType === "unapply" &&
													payment.PaymentMethodType === null &&
													!payment.IsPrepayment
												}
											>
												<label htmlFor={`check_${payment.Id}`}>
													<input
														id={`check_${payment.Id}`}
														type="checkbox"
														name={index}
														checked={payment._checked}
														onChange={this.toggleCheck}
														onKeyPress={this.onKeyPress}
													/>
												</label>
											</If>
										</div>
										<div
											className={`col-sm-2 ${transactionType === "unapply" ? "col-xs-10" : "col-xs-12"}`}
										>
											{utils.formatDate(
												`${payment.PaymentDate}Z`,
												"en-US",
												user.TimeZoneId,
												"numeric",
											)}
										</div>
										<div className="col-xs-12 col-sm-2">
											<strong className="visible-xs-inline">{`${lang.t("Amount")}: `}</strong>
											{utils.currencyFormatter(payment.Amount)}
											<If
												condition={
													invoiceVersion === 3 && payment.PaymentStatusId
												}
											>
												<br className="hidden-xs" />
												<span
													className={`label ${this.renderStatusLabel(payment.PaymentStatusId)}`}
												>
													{payment.PaymentStatus}
												</span>
											</If>
										</div>
										<div className="col-xs-12 col-sm-3">
											<strong className="visible-xs-inline">{`${lang.t("Created By")}: `}</strong>
											{payment.PaymentMethodType !== null
												? "RollKall"
												: payment.CreatedByUserName}
										</div>
										<div
											className={`col-xs-12 ${transactionType === "unapply" ? "col-sm-4" : "col-sm-5"}`}
										>
											{payment.Comments}
										</div>
									</div>
								</For>
							</div>
						</When>
						<Otherwise>
							<If condition={invoiceVersion === 3 || transactionType === "unapply"}>
								<div className="row text-center text-muted">
									{lang.t("No payments found")}
								</div>
							</If>
						</Otherwise>
					</Choose>
				</Modal.Body>
				<Modal.Footer>
					<If
						condition={
							!isFromPaymentPortal &&
							paymentHistory?.some(payment => payment._checked === true)
						}
					>
						<button
							type="button"
							className="btn btn-primary"
							onClick={this.validateInputs}
							disabled={loading}
						>
							{lang.t("Unapply")}
						</button>
					</If>
					<button
						type="button"
						className="btn btn-primary"
						onClick={this.closeModal}
					>
						{lang.t("Done")}
					</button>
				</Modal.Footer>
				<Loader
					active={loading}
					size="small"
				/>
			</Modal>
		);
	}
}

ApplyPaymentsModal.propTypes = {
	user: PropTypes.object,
	callback: PropTypes.func,
	invoiceVersion: PropTypes.number,
	refundsInTransit: PropTypes.array,
	disabledManualPayments: PropTypes.bool,
};

ApplyPaymentsModal.defaultProps = {
	user: {},
	callback: undefined,
	invoiceVersion: undefined,
	refundsInTransit: [],
	disabledManualPayments: false,
};

export default ApplyPaymentsModal;
